// =============================================================================
// 自前地形の山岳島。数式(FBMリッジ+放射フォールオフ)から「描画メッシュ」と
// 「当たり判定Heightfield」を同じ高さ関数で生成する(見た目と足場が完全一致)。
// 島クローンは平行移動しかできない(TSL地形制約)ため、劇的な山のシルエットはこれで作る。
// Safari(WebKit)対策: 非indexed・全属性count一致・頂点カラー(uv/テクスチャ不使用)。
// =============================================================================
import { harvestTHREE, harvestTexturedMat } from './mesh3d';
import type { Heightfield } from './types';

// 決定論ハッシュノイズ(値ノイズ+FBM)。Math.random不使用=リロードで同じ島。
function makeNoise(seed: number) {
  const hash = (ix: number, iz: number): number => {
    let h = (ix * 374761393 + iz * 668265263 + seed * 974711) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return (((h ^ (h >>> 16)) >>> 0) % 100000) / 100000;
  };
  const sstep = (t: number) => t * t * (3 - 2 * t);
  const noise2 = (x: number, z: number): number => {
    const ix = Math.floor(x), iz = Math.floor(z);
    const fx = sstep(x - ix), fz = sstep(z - iz);
    const a = hash(ix, iz), b = hash(ix + 1, iz), c = hash(ix, iz + 1), d = hash(ix + 1, iz + 1);
    return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
  };
  return (x: number, z: number, octaves = 4): number => { // FBM ~0..1
    let v = 0, amp = 0.5, f = 1;
    for (let o = 0; o < octaves; o++) { v += noise2(x * f, z * f) * amp; amp *= 0.5; f *= 2; }
    return v / (1 - Math.pow(0.5, octaves));
  };
}

export interface MountainIsland { group: any; hf: Heightfield }

// 島の性格を決めるオプション(省略時=従来の山岳島と数値まで完全一致=挙動不変)。
//   grid/hfN     : 描画/当たり判定の解像度(大きい島は上げる)
//   falloff      : 放射フォールオフ指数 e = base + swing*sin(ang+phase)(方角で浜⇔断崖)
//   ridgeAmp     : 尾根の強さ(小さいほどなだらかな丘陵)
//   noiseScale   : 起伏の空間周波数(大きい島は下げてゆったりした地形に)
//   colors       : 'mountain'=砂→緑→露岩→峰の岩肌 / 'green'=広い浜と草原の島
export interface TerrainIslandOpts {
  R?: number; peak?: number; seed?: number;
  grid?: number; hfN?: number;
  falloff?: { base: number; swing: number; phase: number };
  ridgeAmp?: number;
  noiseScale?: number;
  noiseTaper?: number; // 0..1: 山頂に向けて起伏を減衰(高所ほど滑らか=必ず登れる峰)。0=従来
  detailAmp?: number;  // 細部ノイズn2の係数(既定0.14)。高周波なので登坂性に効く
  shelfStart?: number; // 外洋海底への遷移開始半径(既定0.84)。浜が広い島は汀線(≈0.90)まで遅らせ陸上の崖を防ぐ
  colors?: 'mountain' | 'green';
}

// (cx,cz)に半径R・峰高PEAKの自前地形の島を作る。戻り値のhfはgroundHeight()にそのまま渡せる。
export function buildMountainIsland(scene: any, cx: number, cz: number, opts: TerrainIslandOpts = {}): MountainIsland | null {
  const T = harvestTHREE(scene);
  if (!T || !T.StdMat || !T.BufferGeometry) return null;
  const R = opts.R ?? 760, PEAK = opts.peak ?? 340, seed = opts.seed ?? 7;
  const fo = opts.falloff ?? { base: 1.9, swing: 0.9, phase: 2.4 };
  const ridgeAmp = opts.ridgeAmp ?? 0.34;
  const noiseTaper = opts.noiseTaper ?? 0;
  const detailAmp = opts.detailAmp ?? 0.14;
  const shelfStart = opts.shelfStart ?? 0.84;
  const ns = opts.noiseScale ?? 0.004;   // n2(細部)は従来比3.25倍の周波数を維持
  const colorProfile = opts.colors ?? 'mountain';
  const fbm = makeNoise(seed);
  const SEA_FLOOR = -85;
  const DEEP_FLOOR = -240; // 外洋の海底(main.tsのSEABED_MINと一致させる)

  // 高さ関数: 放射フォールオフ^指数(方角で変化=南は遠浅の浜/北は断崖) × (基部 + リッジノイズ)
  // 島棚(-85)で途切れると当たり判定の裾野(-240へ下る)より見た目が上に残り、縁の外で潜ると
  // 「見えている砂面の下」に入れてしまう(海底貫通)。外周リングで外洋海底へ連続に下げる。
  const heightAt = (x: number, z: number): number => {
    const dx = x - cx, dz = z - cz;
    const r = Math.hypot(dx, dz) / R;
    if (r >= 1) return DEEP_FLOOR;
    const ang = Math.atan2(dz, dx);
    const e = fo.base + fo.swing * Math.sin(ang + fo.phase); // 方角で指数を変える=南は遠浅の浜/北は断崖
    const t = Math.pow(Math.max(0, 1 - r), e);
    const n = fbm(x * ns, z * ns);                           // 大きめのリッジ
    const n2 = fbm(x * ns * 3.25 + 40, z * ns * 3.25 - 40, 3); // 細部
    const ridge = 1 - Math.abs(2 * n - 1);                   // 尾根強調
    // noiseTaper: 高所(t大)ほど起伏を弱める(二乗で中腹から効かせる)。基本勾配が最大の高所でノイズの壁を作らない
    const nm0 = 1 - noiseTaper * t, nm = nm0 * nm0;
    const h = SEA_FLOOR + (PEAK - SEA_FLOOR) * t * (0.62 + (ridgeAmp * ridge + detailAmp * n2) * nm);
    if (r > shelfStart) {
      const s = (r - shelfStart) / (1 - shelfStart), k = s * s * (3 - 2 * s); // smoothstepでDEEP_FLOORへ
      return h + (DEEP_FLOOR - h) * k;
    }
    return h;
  };

  // --- Heightfield(当たり判定) ---
  const N = opts.hfN ?? 128;
  const minX = cx - R, minZ = cz - R, cell = (2 * R) / N;
  const H = new Float32Array(N * N);
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const x = minX + (i + 0.5) * cell, z = minZ + (j + 0.5) * cell;
    const h = heightAt(x, z);
    H[j * N + i] = h > DEEP_FLOOR + 1 ? h : -Infinity; // 外洋海底(-240)に達した所から「島の外」扱い
  }
  const hf: Heightfield = { minX, minZ, cellX: cell, cellZ: cell, N, H, bounds: { minX, maxX: cx + R, minZ, maxZ: cz + R } };

  // --- 描画メッシュ ---
  // いかだ等で動作実績のある経路と完全同一にする:
  //   indexedで組む → toNonIndexed → computeVertexNormals → 色をあとから塗る。
  const G = opts.grid ?? 96; // 描画解像度(G+1 × G+1 頂点)
  const gc = (2 * R) / G;
  const pos: number[] = [], idx: number[] = [];
  const vidx = new Int32Array((G + 1) * (G + 1)).fill(-1);
  const vAt = (i: number, j: number): number => {
    const k = j * (G + 1) + i;
    if (vidx[k] >= 0) return vidx[k];
    const x = minX + i * gc, z = minZ + j * gc;
    vidx[k] = pos.length / 3;
    pos.push(x - cx, heightAt(x, z), z - cz);
    return vidx[k];
  };
  for (let j = 0; j < G; j++) for (let i = 0; i < G; i++) {
    const x0 = minX + i * gc, z0 = minZ + j * gc, x1 = x0 + gc, z1 = z0 + gc;
    // 全て海底のセルはスキップ(頂点数節約)
    if (heightAt(x0, z0) <= DEEP_FLOOR + 1 && heightAt(x1, z1) <= DEEP_FLOOR + 1 && heightAt(x0, z1) <= DEEP_FLOOR + 1 && heightAt(x1, z0) <= DEEP_FLOOR + 1) continue;
    const a = vAt(i, j), b = vAt(i, j + 1), c = vAt(i + 1, j + 1), d = vAt(i + 1, j);
    idx.push(a, b, c, a, c, d);
  }
  let geo: any = new T.BufferGeometry();
  geo.setAttribute('position', new T.BufferAttribute(new Float32Array(pos), 3));
  geo.setIndex(idx);
  geo = geo.toNonIndexed ? geo.toNonIndexed() : geo; // WebKit対策: 全属性countを揃える
  geo.computeVertexNormals();
  // computeVertexNormals(非indexed)はフラットシェーディングになる。
  // countはそのままに「値だけ」高さ関数の解析勾配で上書きしてスムーズシェーディングにする。
  {
    const pa = geo.attributes.position, na = geo.attributes.normal, EPS = gc * 0.5;
    for (let i = 0; i < pa.count; i++) {
      const x = pa.getX(i) + cx, z = pa.getZ(i) + cz;
      const nx = (heightAt(x - EPS, z) - heightAt(x + EPS, z)) / (2 * EPS);
      const nz = (heightAt(x, z - EPS) - heightAt(x, z + EPS)) / (2 * EPS);
      const il = 1 / Math.hypot(nx, 1, nz);
      na.setXYZ(i, nx * il, il, nz * il);
    }
    na.needsUpdate = true;
  }
  // UV: worldスケールのタイリング(岩テクスチャ用)。position countと同数=Safari安全。
  {
    const pa = geo.attributes.position;
    const n = pa.count, uvA = new Float32Array(n * 2), TILE = colorProfile === 'green' ? 430 : 190;
    for (let i = 0; i < n; i++) { uvA[i * 2] = (pa.getX(i) + cx) / TILE; uvA[i * 2 + 1] = (pa.getZ(i) + cz) / TILE; }
    geo.setAttribute('uv', new T.BufferAttribute(uvA, 2));
  }
  // 色: 非indexed後の頂点位置から高度・傾斜で塗り分け(砂→緑→岩→峰の岩肌)
  {
    const pa = geo.attributes.position;
    const n = pa.count, colA = new Float32Array(n * 3), EPS = 3;
    for (let i = 0; i < n; i++) {
      const x = pa.getX(i) + cx, z = pa.getZ(i) + cz, h = pa.getY(i);
      const nx = (heightAt(x - EPS, z) - heightAt(x + EPS, z)) / (2 * EPS);
      const nz = (heightAt(x, z - EPS) - heightAt(x, z + EPS)) / (2 * EPS);
      const slope = Math.hypot(nx, nz);
      // 高度・傾斜で塗り分け(露出が高い環境なので全体を濃いめに)
      let cr = 0.60, cg = 0.52, cb = 0.36;                                    // 基本色
      let shade: number;
      if (colorProfile === 'green') {
        // 広い浜と草原の島: 浜を広く(h<10)、明るい緑の草原、露岩は控えめ。
        // メイン島の地形テクスチャ(Material.001=砂と緑)との乗算前提。まだらは中程度
        if (h < 10) { cr = 1.05; cg = 0.98; cb = 0.80; }                       // 広い浜
        else if (h > 18 && slope > 0.55) { cr = 0.42; cg = 0.39; cb = 0.32; }  // まれな露岩
        else if (h > 160) { cr = 0.55; cg = 0.58; cb = 0.34; }                // 丘の上=乾いた草
        else { cr = 0.46; cg = 0.62; cb = 0.30; }                             // 草原
        shade = 0.78 + 0.36 * fbm(x * 0.02, z * 0.02, 2);
      } else {
        if (h < 14) { cr = 1.0; cg = 0.92; cb = 0.72; }                        // 麓の浜=明るい砂色
        if (h > 24 && slope > 0.42) { cr = 0.26; cg = 0.23; cb = 0.20; }       // 岩(急斜面の露岩)
        else if (h > 185) { cr = 0.34; cg = 0.31; cb = 0.28; }                // 峰の岩肌
        else if (h > 20) { cr = 0.22; cg = 0.35; cb = 0.17; }                 // 中腹の緑(広め)
        shade = 0.68 + 0.5 * fbm(x * 0.02, z * 0.02, 2);                      // 強めのまだら
      }
      colA[i * 3] = cr * shade; colA[i * 3 + 1] = cg * shade; colA[i * 3 + 2] = cb * shade;
    }
    geo.setAttribute('color', new T.BufferAttribute(colA, 3));
  }
  // 実テクスチャを流用して頂点カラー(高度の塗り分け)と乗算 → のっぺり感を消す。
  // mountain=RocksBig(岩肌) / green=メイン島の地形面 Material.001(砂と緑の自然なディテール)
  const texSrc = colorProfile === 'green'
    ? (harvestTexturedMat(scene, /^Material\.001$/) || harvestTexturedMat(scene, /^RocksBig$/i))
    : harvestTexturedMat(scene, /^RocksBig$/i);
  const mat = new T.StdMat({ color: 0xffffff, roughness: 0.96, metalness: 0, vertexColors: true, side: 2, map: texSrc ? texSrc.map : null });
  const mesh = new T.Mesh(geo, mat);
  mesh.position.set(cx, 0, cz);
  mesh.frustumCulled = false;
  mesh.name = 'game-mountain-island';
  const group = new T.Group(); group.add(mesh); group.name = 'game-mountain';
  scene.add(group);
  return { group, hf };
}
